/**
 * @file IntegrationSwitchesPage.tsx
 * @brief Super Admin → Integrations (W32, BACKEND B42): turn Zapier or Make
 *        off for every company, or for one company. Web and the Mac app.
 *
 * @details
 * Top: one card per app with its own logo (public/brand/partners, unmodified),
 * the platform switch and what it reaches (connections, active hooks, events
 * waiting). Every flip asks first. When the server's env master switch is off
 * the card says so and the switch is read-only (only a deploy changes it).
 *
 * Below: every company with its `zapier` / `make` switch (the same value as
 * that company's Company → Features) and its live connections. Search by name
 * or id, 25 a page. Each flip asks first.
 *
 * @references
 * - scratch/claude-code-report.md "## B42" (contract), ADR-060
 * - features/integrations/IntegrationsPage.tsx (the user side)
 *
 * @author DES (prepared with Claude)
 * @date 30-10-2026
 */

import { useMemo, useState, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, LoaderCircle, Search } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { SkeletonFields } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import { DataTable, type PaginationState } from "@/components/data/DataTable";
import { GridBand, GridBlock, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { MakeLogo } from "@/features/integrations/MakeLogo";
import { ZapierWordmark } from "@/features/integrations/ZapierWordmark";
import { ApiClientError } from "@/shared/api/client";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { toast } from "@/shared/hooks/useToast";
import { formatRelative } from "@/shared/utils/formatters";
import {
  useIntegrationCompanies,
  useIntegrationPlatform,
  useSetCompanyIntegration,
  useSetIntegrationPlatform,
} from "./api";
import {
  APP_NAME,
  INTEGRATION_APPS,
  companyConfirm,
  companyUsage,
  platformConfirm,
  platformStatus,
  serverOffLine,
  totalPages,
  type ConfirmCopy,
  type IntegrationApp,
  type IntegrationCompany,
  type PlatformApp,
} from "./integrationSwitches";

const LOGO: Record<IntegrationApp, ReactNode> = {
  zapier: <ZapierWordmark height={26} />,
  make: <MakeLogo height={24} />,
};

const PAGE_SIZE = 25;

const errorText = (err: unknown) => (err instanceof ApiClientError ? err.message : "Please try again.");

/** One pending confirm: its words and what Confirm does. */
type Pending = { copy: ConfirmCopy; run: () => Promise<void> };

function ConfirmSwitch({ pending, onClose }: { pending: Pending | null; onClose: () => void }) {
  return (
    <AlertDialog open={pending != null} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{pending?.copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{pending?.copy.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const run = pending?.run;
              onClose();
              void run?.();
            }}
          >
            {pending?.copy.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="min-w-0">
      <div className="text-lg font-semibold tabular-nums">{value.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function PlatformCard({
  app,
  a,
  busy,
  onToggle,
}: {
  app: IntegrationApp;
  a: PlatformApp;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  const status = platformStatus(a);
  return (
    <GridCell className="flex flex-col gap-4">
      <div className="flex min-h-8 items-center gap-3">
        {LOGO[app]}
        <Badge variant={status.tone} className="ml-auto">
          {status.label}
        </Badge>
      </div>
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 bg-muted/40 px-3.5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            {APP_NAME[app]} for every company
            {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {!a.serverEnabled
              ? serverOffLine(app)
              : a.platformEnabled
                ? `On. Companies that allow ${APP_NAME[app]} can connect and use it.`
                : `Paused. Nobody can connect or use ${APP_NAME[app]}; events wait until it is back on.`}
          </p>
        </div>
        <Switch
          checked={a.serverEnabled && a.platformEnabled}
          disabled={busy || !a.serverEnabled}
          onCheckedChange={onToggle}
          aria-label={`${APP_NAME[app]} for every company`}
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat value={a.connections} label="Connections" />
        <Stat value={a.hooks} label={app === "zapier" ? "Active Zaps" : "Active scenarios"} />
        <Stat value={a.pendingDeliveries} label="Events waiting" />
      </div>
      <p className="text-xs text-muted-foreground">
        {a.updatedAt ? `Last changed ${formatRelative(a.updatedAt)}` : "Never changed here"}
      </p>
    </GridCell>
  );
}

export function IntegrationSwitchesPage() {
  const platform = useIntegrationPlatform();
  const setPlatform = useSetIntegrationPlatform();
  const setCompany = useSetCompanyIntegration();
  const [pending, setPending] = useState<Pending | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim(), 350);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const missing = platform.data?.status === "missing";
  const apps = platform.data?.status === "ok" ? platform.data.apps : null;
  const companies = useIntegrationCompanies(search, page, limit);

  const askPlatform = (app: IntegrationApp, a: PlatformApp, next: boolean) =>
    setPending({
      copy: platformConfirm(app, next, a),
      run: async () => {
        try {
          await setPlatform.mutateAsync({ [app]: next });
          toast({ variant: "success", title: next ? `${APP_NAME[app]} is on` : `${APP_NAME[app]} is paused` });
        } catch (err) {
          toast({ variant: "destructive", title: "Could not save", description: errorText(err) });
        }
      },
    });

  const askCompany = (app: IntegrationApp, c: IntegrationCompany, next: boolean) =>
    setPending({
      copy: companyConfirm(app, next, c),
      run: async () => {
        try {
          await setCompany.mutateAsync({ id: c.id, patch: { [app]: next } });
          toast({
            variant: "success",
            title: next ? `${APP_NAME[app]} allowed for ${c.name}` : `${APP_NAME[app]} off for ${c.name}`,
          });
        } catch (err) {
          toast({ variant: "destructive", title: "Could not save", description: errorText(err) });
        }
      },
    });

  const columns = useMemo<ColumnDef<IntegrationCompany>[]>(() => {
    const appColumn = (app: IntegrationApp): ColumnDef<IntegrationCompany> => ({
      id: app,
      header: APP_NAME[app],
      cell: ({ row }) => {
        const c = row.original;
        const busy = setCompany.isPending && setCompany.variables?.id === c.id && app in setCompany.variables.patch;
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={c[app].enabled}
              disabled={busy}
              onCheckedChange={(next) => askCompany(app, c, next)}
              aria-label={`${APP_NAME[app]} for ${c.name}`}
            />
            {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
          </div>
        );
      },
    });
    return [
      {
        id: "company",
        header: "Company",
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 shrink-0 text-brand" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{c.name}</p>
                {c.status && c.status !== "active" ? (
                  <p className="truncate text-xs capitalize text-muted-foreground">{c.status}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      appColumn("zapier"),
      appColumn("make"),
      {
        id: "connections",
        header: "Active connections",
        cell: ({ row }) => (
          <div className="space-y-0.5 text-xs tabular-nums">
            {INTEGRATION_APPS.map((app) => (
              <div key={app}>
                <span className="font-medium">{APP_NAME[app]}</span>{" "}
                <span className="text-muted-foreground">{companyUsage(app, row.original[app])}</span>
              </div>
            ))}
          </div>
        ),
      },
    ];
    // askCompany only closes over setters and the mutation; the busy spinner needs its state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCompany.isPending, setCompany.variables]);

  const pagination: PaginationState | undefined = companies.data
    ? {
        page: companies.data.page,
        limit: companies.data.limit,
        total: companies.data.total,
        totalPages: totalPages(companies.data),
      }
    : undefined;

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Integrations</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Pause Zapier or Make for every company, or turn them on or off for one company. While paused,
            connections are kept and events wait; they go out when you turn it back on.
          </p>
        </GridBand>

        {platform.isLoading ? (
          <GridCell>
            <SkeletonFields fields={4} />
          </GridCell>
        ) : platform.isError ? (
          <GridCell>
            <p className="text-sm text-destructive">Couldn&apos;t load the integration switches. Refresh and try again.</p>
          </GridCell>
        ) : !apps ? (
          <GridCell>
            <p className="text-sm text-muted-foreground">
              These switches need a newer backend (B42). Nothing to change here yet.
            </p>
          </GridCell>
        ) : (
          <GridCells className="grid-cols-1 md:grid-cols-2">
            {INTEGRATION_APPS.map((app) => {
              const a = apps[app];
              return (
                <PlatformCard
                  key={app}
                  app={app}
                  a={a}
                  busy={setPlatform.isPending && app in (setPlatform.variables ?? {})}
                  onToggle={(next) => askPlatform(app, a, next)}
                />
              );
            })}
          </GridCells>
        )}

        {missing ? null : (
          <>
            <GridBand className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1 basis-64">
                <h3 className="text-base font-semibold">Companies</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  The same switch as each company&apos;s Company → Features. A paused app stays paused for
                  everyone, whatever is set here.
                </p>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by company name or ID"
                  aria-label="Search companies"
                  className="pl-9"
                />
              </div>
            </GridBand>
            <GridCell padded={false} className="border-b-0">
              <DataTable<IntegrationCompany, unknown>
                variant="cell"
                columns={columns}
                data={companies.data?.companies ?? []}
                isLoading={companies.isLoading}
                isFetching={companies.isFetching}
                error={companies.error}
                onRetry={() => void companies.refetch()}
                rowKey={(c) => c.id}
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(next) => {
                  setLimit(next);
                  setPage(1);
                }}
                emptyTitle={search ? "No companies match that search" : "No companies yet"}
                emptyDescription={search ? "Try a shorter name or the company ID." : undefined}
              />
            </GridCell>
          </>
        )}
      </GridBlock>

      <ConfirmSwitch pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}
